/**
 * RiderAccountingPage Component
 * 라이더 출금제한 관리 페이지
 */
import React, { useState, useMemo, useCallback } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import { useTableSort } from '@/shared/hooks/useTableSort';
import { useRiderData } from '../hooks/useRiderData';
import { RiderListItem } from '../types/riderAccounting.types';
import { accountingUtils } from '../utils/accountingUtils';
import FilterSection from './FilterSection';
import RiderTableRow from './RiderTableRow';
import '../styles/AccountingRider.css';

const ITEMS_PER_PAGE = 10;

const TABLE_HEADERS = [
    { key: 'suppName', label: '협력사명', sortable: false },
    { key: 'riderName', label: '라이더명', sortable: false },
    { key: 'contStatus', label: '계약상태', sortable: false },
    { key: 'riderId', label: '라이더ID', sortable: false },
    { key: 'preAcctYn', label: '선정산', sortable: false },
    { key: 'riderInsDate', label: '등록일자', sortable: true },
    { key: 'contEdDate', label: '계약종료일자', sortable: true },
    { key: 'balAmt', label: '잔액', sortable: true },
    { key: 'unpaidAmt', label: '미납금', sortable: true },
    { key: 'action', label: '계좌관리', sortable: false },
] as const;

interface ConfirmModalState {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
}

const RiderAccountingPage: React.FC = () => {
    const {
        searchRiderKeyword,
        setSearchRiderKeyword,
        searchContractStatus,
        setSearchContractStatus,
        searchPartnerId,
        setSearchPartnerId,
        searchWithdrawalStatus,
        setSearchWithdrawalStatus,
        allData,
        loading,
        userType,
        contractStatusCodes,
        contractStatusLoading,
        partnerNames,
        handleSearch,
        executeWithdrawalLimit,
        fetchRiderList,
        resetSearchConditions,
    } = useRiderData();

    const {
        getRiderContractStatusBadge,
        formatNumber,
        handleSendMoney,
        getRiderContractStatusBadgeClass,
        getPreAccountBadgeClass,
        getPreAccountBadgeText,
        getRiderRowClassName,
        isRiderAccountDisabled,
    } = accountingUtils;

    const {
        sortConfig,
        sortedData: sortedAllData,
        handleSort: onSort,
    } = useTableSort<RiderListItem>({
        data: allData || [],
        numericColumns: ['balAmt', 'unpaidAmt'],
        dateColumns: ['riderInsDate', 'contEdDate'],
    });

    const [currentPage, setCurrentPage] = useState(1);

    const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    const handleSort = useCallback((columnKey: string) => {
        onSort(columnKey);
        setCurrentPage(1);
    }, [onSort]);

    const currentPageData = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedAllData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedAllData, currentPage]);

    const totalPages = useMemo(
        () => Math.ceil(sortedAllData.length / ITEMS_PER_PAGE),
        [sortedAllData.length]
    );

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handleSearchWithReset = useCallback(() => {
        setCurrentPage(1);
        handleSearch();
    }, [handleSearch]);

    const handleReset = useCallback(async () => {
        resetSearchConditions();
        setCurrentPage(1);
        await fetchRiderList();
    }, [resetSearchConditions, fetchRiderList]);

    const handleWithdrawalLimit = useCallback((
        riderName: string,
        riderId: string,
        suppId: string,
        withdrawalYn: string | null,
        targetAction: 'ALLOW' | 'RESTRICT'
    ) => {
        const newStatus: 'Y' | 'N' = targetAction === 'ALLOW' ? 'N' : 'Y';
        const action = targetAction === 'ALLOW' ? '출금허용' : '출금제한';
        const consequence = targetAction === 'RESTRICT'
            ? '출금제한하시면 해당 라이더는 더 이상 출금할 수 없습니다.'
            : '출금허용하시면 해당 라이더는 다시 출금할 수 있습니다.';

        setConfirmModal({
            isOpen: true,
            title: `${riderName} ${action}`,
            message: `${riderName}을(를) ${action} 하시겠습니까? ${consequence}`,
            onConfirm: () => {
                setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                void executeWithdrawalLimit(riderId, suppId, newStatus, riderName, action);
            },
        });
    }, [executeWithdrawalLimit]);

    const closeConfirmModal = useCallback(() => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    }, []);

    if (loading) {
        return (
            <div className="account-rider">
                <div className="loading-container">
                    <Loading message="라이더 정보를 불러오는 중..." />
                </div>
            </div>
        );
    }

    return (
        <div className="account-rider">
            {userType === 'SUPPLIER' && (
                <div className="page-header">
                    <div className="page-title-container">
                        <h1 className="page-title">출금제한</h1>
                    </div>
                </div>
            )}

            <FilterSection
                userType={userType}
                searchPartnerId={searchPartnerId}
                searchRiderKeyword={searchRiderKeyword}
                searchContractStatus={searchContractStatus}
                searchWithdrawalStatus={searchWithdrawalStatus}
                partnerNames={partnerNames}
                contractStatusCodes={contractStatusCodes}
                contractStatusLoading={contractStatusLoading}
                loading={loading}
                onPartnerIdChange={setSearchPartnerId}
                onRiderKeywordChange={setSearchRiderKeyword}
                onContractStatusChange={setSearchContractStatus}
                onWithdrawalStatusChange={setSearchWithdrawalStatus}
                onSearch={handleSearchWithReset}
                onReset={handleReset}
            />

            <div className="card">
                <div className="card-body">
                    <div className="table-container">
                        <table className="table sortable-table">
                            <thead>
                            <tr>
                                {TABLE_HEADERS.map((header) => {
                                    const isSorted = sortConfig.columnKey === header.key;
                                    const direction = isSorted ? sortConfig.direction : null;
                                    return (
                                        <th
                                            key={header.key}
                                            className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''}`}
                                            onClick={() => header.sortable && handleSort(header.key)}
                                        >
                                            {header.label}
                                            {header.sortable && <SortIcon direction={direction} />}
                                        </th>
                                    );
                                })}
                            </tr>
                            </thead>
                            <tbody>
                            {!currentPageData || currentPageData.length === 0 ? (
                                <tr>
                                    <td colSpan={TABLE_HEADERS.length} className="text-center">
                                        등록된 라이더가 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                currentPageData.map((rider, index) => (
                                    <RiderTableRow
                                        key={`${rider.riderId}-${index}`}
                                        rider={rider}
                                        index={index}
                                        isTerminatedContract={isRiderAccountDisabled(rider.contStatus)}
                                        getRiderRowClassName={getRiderRowClassName}
                                        getRiderContractStatusBadgeClass={getRiderContractStatusBadgeClass}
                                        getRiderContractStatusBadge={getRiderContractStatusBadge}
                                        getPreAccountBadgeClass={getPreAccountBadgeClass}
                                        getPreAccountBadgeText={getPreAccountBadgeText}
                                        formatNumber={formatNumber}
                                        onSendMoney={handleSendMoney}
                                        onWithdrawalLimit={handleWithdrawalLimit}
                                    />
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxPagesToShow={5}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm ?? closeConfirmModal}
                onCancel={closeConfirmModal}
            />
        </div>
    );
};

export default RiderAccountingPage;
